import { zodResolver } from '@hookform/resolvers/zod';
import { projectCreateSchema, type Project, type ProjectCreateInput } from '@plane/shared';
import type { z } from 'zod';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { Field } from '@/components/field';
import { FotoDoProjeto } from '@/modules/projects/components/foto-do-projeto';
import {
  useCreateProject,
  useProjects,
  useRemoveImage,
  useUpdateProject,
  useUploadImage,
} from '@/modules/projects/hooks/use-projects';
import { medidaOuNulo } from '@/modules/projects/medida';
import { prepararImagem } from '@/modules/projects/preparar-imagem';
import { ApiRequestError } from '@/services/http-client';

/**
 * O que o formulário manipula, antes das defaults do Zod.
 *
 * `measurement` e `published` têm `.default()` no contrato, então o tipo de
 * ENTRADA os traz opcionais e o de SAÍDA os traz obrigatórios. O React Hook Form
 * precisa dos dois separados, senão o resolver não encaixa.
 */
type ProjetoFormulario = z.input<typeof projectCreateSchema>;

function mensagemDeErro(erro: unknown): string {
  if (erro instanceof ApiRequestError) return erro.message;
  if (erro instanceof Error) return erro.message;

  return 'Não foi possível falar com o servidor. Verifique a conexão e tente de novo.';
}

export function ProjectFormPage() {
  const { id } = useParams<{ id: string }>();

  return id === undefined ? <Criacao /> : <Edicao id={id} />;
}

function Casca({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <div>
        <Link to="/projetos" className="text-sm text-brand-700 underline">
          ← Projetos
        </Link>
        <h1 className="mt-2 font-display text-2xl">{titulo}</h1>
      </div>

      {children}
    </div>
  );
}

function CamposDoProjeto({
  register,
  errors,
}: {
  register: ReturnType<typeof useForm<ProjetoFormulario>>['register'];
  errors: ReturnType<typeof useForm<ProjetoFormulario>>['formState']['errors'];
}) {
  return (
    <>
      <Field
        id="title"
        label="Nome do projeto"
        placeholder="Cozinha Marquês"
        error={errors.title?.message}
        {...register('title')}
      />

      <div className="flex flex-col gap-1.5">
        <label htmlFor="specs" className="text-xs tracking-[0.12em] text-ink-soft uppercase">
          Materiais e acabamentos
        </label>
        <textarea
          {...register('specs')}
          id="specs"
          rows={3}
          placeholder="MDF Carvalho Hanover · bancada em granito absoluto · torre quente Häfele"
          aria-invalid={errors.specs ? true : undefined}
          aria-describedby={errors.specs ? 'specs-error' : undefined}
          className="border border-clay bg-white px-3 py-2.5 text-sm transition-colors focus:border-brand-600 focus:outline-none aria-invalid:border-brass"
        />
        {errors.specs && (
          <p id="specs-error" role="alert" className="text-xs text-brass">
            {errors.specs.message}
          </p>
        )}
      </div>

      <div className="grid gap-5 sm:grid-cols-2">
        <Field
          id="place"
          label="Onde foi entregue"
          placeholder="Ed. Marquês"
          error={errors.place?.message}
          {...register('place')}
        />

        <Field
          id="year"
          type="number"
          label="Ano"
          placeholder="2024"
          error={errors.year?.message}
          {...register('year', { valueAsNumber: true })}
        />
      </div>

      <Field
        id="measurement"
        label="Medida em destaque (opcional)"
        placeholder="4.120 mm"
        error={errors.measurement?.message}
        {...register('measurement', { setValueAs: medidaOuNulo })}
      />
    </>
  );
}

function Criacao() {
  const navigate = useNavigate();
  const criar = useCreateProject();
  const [erro, setErro] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ProjetoFormulario, unknown, ProjectCreateInput>({
    resolver: zodResolver(projectCreateSchema),
    defaultValues: {
      title: '',
      specs: '',
      place: '',
      year: new Date().getFullYear(),
      measurement: null,
      published: false,
    },
  });

  const onSubmit = handleSubmit(async (valores) => {
    setErro(null);

    try {
      const criado = await criar.mutateAsync(valores);

      // Vai direto para a edição: as fotos só podem ser anexadas depois que o
      // projeto existe, e mandar a pessoa de volta à lista esconderia isso.
      void navigate(`/projetos/${criado.id}`, { replace: true });
    } catch (falha) {
      setErro(mensagemDeErro(falha));
    }
  });

  return (
    <Casca titulo="Novo projeto">
      <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
        {erro && (
          <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
            {erro}
          </p>
        )}

        <CamposDoProjeto register={register} errors={errors} />

        <p className="text-xs text-ink-soft">
          O projeto nasce como rascunho. As fotos entram na próxima tela, e publicar é um passo
          separado.
        </p>

        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-brand-600 px-5 py-3 text-sm text-bone transition-colors hover:bg-brand-700 disabled:opacity-60"
          >
            {isSubmitting ? 'Criando…' : 'Criar e adicionar fotos'}
          </button>

          <Link to="/projetos" className="text-sm text-ink-soft underline">
            Cancelar
          </Link>
        </div>
      </form>
    </Casca>
  );
}

function Edicao({ id }: { id: string }) {
  const navigate = useNavigate();
  const atualizar = useUpdateProject();
  const consulta = useProjects();
  const projeto = consulta.data?.find((p) => p.id === id);

  const [erro, setErro] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ProjetoFormulario, unknown, ProjectCreateInput>({
    resolver: zodResolver(projectCreateSchema),
    values: projeto
      ? {
          title: projeto.title,
          specs: projeto.specs,
          place: projeto.place,
          year: projeto.year,
          measurement: projeto.measurement,
          published: projeto.published,
        }
      : undefined,
  });

  const onSubmit = handleSubmit(async (valores) => {
    setErro(null);

    try {
      await atualizar.mutateAsync({ id, input: valores });
      void navigate('/projetos');
    } catch (falha) {
      setErro(mensagemDeErro(falha));
    }
  });

  if (consulta.isPending) {
    return (
      <Casca titulo="Editar projeto">
        <p role="status" aria-live="polite" className="text-sm text-ink-soft">
          Carregando…
        </p>
      </Casca>
    );
  }

  if (!projeto) {
    return (
      <Casca titulo="Editar projeto">
        <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
          Projeto não encontrado. Ele pode ter sido removido.
        </p>
      </Casca>
    );
  }

  return (
    <Casca titulo={`Editar ${projeto.title}`}>
      <Fotos projeto={projeto} />

      <form
        onSubmit={onSubmit}
        className="flex flex-col gap-5 border-t border-sand pt-6"
        noValidate
      >
        {erro && (
          <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
            {erro}
          </p>
        )}

        <CamposDoProjeto register={register} errors={errors} />

        <div className="flex items-center gap-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="bg-brand-600 px-5 py-3 text-sm text-bone transition-colors hover:bg-brand-700 disabled:opacity-60"
          >
            {isSubmitting ? 'Salvando…' : 'Salvar'}
          </button>

          <Link to="/projetos" className="text-sm text-ink-soft underline">
            Cancelar
          </Link>
        </div>
      </form>
    </Casca>
  );
}

function Fotos({ projeto }: { projeto: Project }) {
  const enviar = useUploadImage();
  const remover = useRemoveImage();
  const entrada = useRef<HTMLInputElement>(null);

  const [alt, setAlt] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [preparando, setPreparando] = useState(false);

  async function aoEscolher(arquivo: File | undefined) {
    if (!arquivo) return;

    setErro(null);

    if (alt.trim().length < 3) {
      setErro('Descreva a imagem antes de escolher o arquivo — ela é lida por quem não enxerga.');

      return;
    }

    setPreparando(true);

    try {
      const imagem = await prepararImagem(arquivo);

      await enviar.mutateAsync({ id: projeto.id, imagem, alt: alt.trim() });
      setAlt('');

      if (entrada.current) entrada.current.value = '';
    } catch (falha) {
      setErro(mensagemDeErro(falha));
    } finally {
      setPreparando(false);
    }
  }

  const ocupado = preparando || enviar.isPending || remover.isPending;

  return (
    <section className="flex flex-col gap-4">
      <div>
        <h2 className="font-display text-lg">Fotos</h2>
        <p className="mt-1 text-xs text-ink-soft">
          A primeira foto é a capa na galeria. São reduzidas para 1600px e convertidas no navegador
          antes de subir.
        </p>
      </div>

      {erro && (
        <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
          {erro}
        </p>
      )}

      {projeto.images.length > 0 && (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {projeto.images.map((imagem) => (
            <li key={imagem.id} className="flex flex-col gap-1">
              <FotoDoProjeto
                url={imagem.url}
                alt={imagem.alt}
                width={imagem.width}
                height={imagem.height}
                className="aspect-[3/2] w-full bg-sand object-cover"
              />
              <p className="truncate text-xs text-ink-soft" title={imagem.alt}>
                {imagem.alt}
              </p>
              <button
                type="button"
                onClick={() =>
                  void remover
                    .mutateAsync({ id: projeto.id, imageId: imagem.id })
                    .catch((falha: unknown) => setErro(mensagemDeErro(falha)))
                }
                disabled={ocupado}
                className="self-start text-xs text-brass underline disabled:opacity-50"
              >
                Remover
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-col gap-3 border border-clay p-4">
        <Field
          id="alt"
          label="Descrição da próxima foto"
          placeholder="Cozinha planejada em madeira com ilha central"
          value={alt}
          onChange={(evento) => setAlt(evento.target.value)}
        />

        <div className="flex flex-col gap-1.5">
          <label htmlFor="arquivo" className="text-xs tracking-[0.12em] text-ink-soft uppercase">
            Arquivo
          </label>
          <input
            ref={entrada}
            id="arquivo"
            type="file"
            accept="image/*"
            disabled={ocupado}
            onChange={(evento) => void aoEscolher(evento.target.files?.[0])}
            className="text-sm file:mr-3 file:border file:border-clay file:bg-bone-soft file:px-3 file:py-1.5 file:text-sm disabled:opacity-50"
          />
        </div>

        {ocupado && (
          <p role="status" aria-live="polite" className="text-xs text-ink-soft">
            {preparando ? 'Preparando a imagem…' : 'Enviando…'}
          </p>
        )}
      </div>
    </section>
  );
}
